import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import {
  ROLE_HINTS,
  ROLE_LABELS,
  TEAM_PROFILES,
  findMemberByEmail,
  isTeamMember,
  memberName,
  setMemberRole,
} from '@/shared/team';
import { cn } from '@/lib/utils';

const SHOWN = 8;

export const initialsOf = (name) =>
  String(name || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

function PersonRow({ person, selected, onSelect }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors duration-(--transition-fast)',
        selected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 hover:bg-muted/50',
      )}
    >
      <Avatar className="size-8">
        <AvatarFallback className="bg-muted text-xs">{initialsOf(person.name || person.email)}</AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{person.name || person.email}</span>
        <span className="block truncate text-xs text-muted-foreground">{person.email}</span>
      </span>
      {selected && <ApperIcon name="CircleCheck" size={18} className="shrink-0 text-primary" />}
    </button>
  );
}

/**
 * Owner-only dialog that turns a signed-up customer into an Admin (or Staff).
 * Pass `candidates` to pick from a list, or `preset` to promote one person.
 */
export default function MakeAdminDialog({ open, onOpenChange, candidates = [], preset = null, onSaved }) {
  const [query, setQuery] = useState('');
  const [pickedId, setPickedId] = useState(null);
  const [profile, setProfile] = useState('Admin_c');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setQuery('');
      setPickedId(preset?.id ?? null);
      setProfile('Admin_c');
    }
  }, [open, preset]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? candidates.filter((m) => `${m.name ?? ''} ${m.email ?? ''}`.toLowerCase().includes(q))
      : candidates;
    return list.slice(0, SHOWN);
  }, [candidates, query]);

  const person = preset ?? candidates.find((m) => String(m.id) === String(pickedId)) ?? null;

  async function submit() {
    if (!person) {
      toast.error('Pick who to make admin');
      return;
    }
    setSaving(true);
    try {
      await setMemberRole(person, profile);
      toast.success(`${memberName(person)} is now ${ROLE_LABELS[profile]}. They see the console next time they sign in.`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || 'Could not change their role');
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={preset ? `Make ${memberName(preset)} an admin?` : 'Make a user admin'}
      description="Pick someone who already has a Ghost Cable sign-in. Their orders and account stay as they are."
      onSubmit={submit}
      saving={saving}
      submitLabel={profile === 'Admin_c' ? 'Make admin' : 'Add as staff'}
      busyLabel="Saving…"
    >
      {preset ? (
        <PersonRow person={preset} selected onSelect={() => {}} />
      ) : (
        <Field label="User" htmlFor="ma-search" hint={`${candidates.length} signed-up ${candidates.length === 1 ? 'user' : 'users'} not on the team`}>
          <div className="space-y-2">
            <div className="relative">
              <ApperIcon
                name="Search"
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                id="ma-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or email"
                className="pl-9"
                autoComplete="off"
              />
            </div>
            <div role="radiogroup" aria-label="User" className="max-h-72 space-y-1.5 overflow-y-auto">
              {matches.length ? (
                matches.map((m) => (
                  <PersonRow
                    key={m.id}
                    person={m}
                    selected={String(m.id) === String(pickedId)}
                    onSelect={() => setPickedId(m.id)}
                  />
                ))
              ) : (
                <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
                  {candidates.length ? 'Nobody matches that search.' : 'No signed-up users yet. Customers appear here once they make an account.'}
                </p>
              )}
            </div>
            {query.trim() === '' && candidates.length > SHOWN && (
              <p className="text-xs text-muted-foreground">Showing {SHOWN} of {candidates.length}. Search to find someone else.</p>
            )}
          </div>
        </Field>
      )}
      <Field label="Role" htmlFor="ma-role" hint={ROLE_HINTS[profile]}>
        <PickSelect
          id="ma-role"
          value={profile}
          onChange={setProfile}
          values={TEAM_PROFILES}
          labels={ROLE_LABELS}
          placeholder="Choose a role"
        />
      </Field>
    </FormDialog>
  );
}

/** "Make admin" on a customer's record. Finds their sign-in by email, then confirms. Owner only. */
export function MakeAdminButton({ email, name, size = 'sm' }) {
  const [finding, setFinding] = useState(false);
  const [member, setMember] = useState(null);
  const [done, setDone] = useState('');

  async function start() {
    if (!email) {
      toast.error('Add an email to this customer first. Their sign-in is matched by email.');
      return;
    }
    setFinding(true);
    try {
      const found = await findMemberByEmail(email);
      if (!found) {
        toast.error(`${name || email} has no Ghost Cable sign-in yet. Ask them to sign up, or invite them from Team.`);
        return;
      }
      if (isTeamMember(found)) {
        toast.info(`${memberName(found)} is already ${ROLE_LABELS[found.profile?.name] ?? 'on the team'}. Change it from Team.`);
        return;
      }
      setMember(found);
    } catch (err) {
      toast.error(err?.message || 'Could not look up their sign-in');
    } finally {
      setFinding(false);
    }
  }

  if (done) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-highlight/40 bg-highlight/15 px-2.5 py-1 text-xs text-highlight">
        <ApperIcon name="ShieldCheck" size={14} />
        {done}
      </span>
    );
  }

  return (
    <>
      <Button variant="outline" size={size} onClick={start} disabled={finding}>
        <ApperIcon name={finding ? 'Search' : 'ShieldPlus'} size={16} />
        {finding ? 'Finding…' : 'Make admin'}
      </Button>
      <MakeAdminDialog
        open={!!member}
        preset={member}
        onOpenChange={(o) => !o && setMember(null)}
        onSaved={() => setDone('On the team')}
      />
    </>
  );
}
